<template>
  <div class="page-content">
    <!-- Page Title -->
    <div class="page-header">
      <h1 class="page-title">Pelaajien tulokset</h1>
      <p class="page-description">Katso kaikkien pelaajien sijoitukset ja pisteet</p>
    </div>

    <div class="results-view-toggle" role="tablist" aria-label="Sijoitusten näkymä">
      <button
        type="button"
        :class="['results-view-button', { active: mainView === 'current' }]"
        @click="setMainView('current')"
      >
        Nykyinen jakso
      </button>
      <button
        type="button"
        :class="['results-view-button', { active: mainView === 'archive' }]"
        @click="setMainView('archive')"
      >
        Aiemmat jaksot
      </button>
    </div>

    <PeriodArchivePage v-if="mainView === 'archive'" :requested-start="requestedStart" />

    <template v-else>
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">Ladataan tuloksia...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <strong>Virhe!</strong>
      <span>{{ error }}</span>
    </div>

    <!-- Empty State with Period Toggle -->
    <div v-else-if="leaderboardData.length === 0" class="empty-state">
      <p class="empty-text">Jakso ei ole vielä alkanut.</p>
      <p v-if="!isAuthenticated" class="auth-prompt">
        <router-link to="/sign-in" class="auth-link">Kirjautumalla</router-link> näet kaikki tulokset.
      </p>
    </div>

    <!-- Leaderboard Table -->
    <div v-else class="leaderboard-container">
      <div class="leaderboard-card">
        <div class="leaderboard-header">
          <h2>Pelaajien tulokset</h2>
        </div>
        
        <div class="table-container">
          <table class="leaderboard-table">
            <thead>
              <tr>
                <th class="rank-column">Sija</th>
                <th class="player-column">Pelaaja</th>
                <th class="points-column">Pisteet</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(player, index) in leaderboardData" :key="player.PlayerID" class="leaderboard-row">
                <td class="rank-column">{{ index + 1 + (currentPage - 1) * pageSize }}</td>
                <td class="player-column">
                  <!-- Current leaderboard players can be opened from the live results view. -->
                  <span 
                    v-if="isAuthenticated" 
                    class="player-name clickable"
                    @click="openPlayerTeamModal(player.PlayerID, player.username); checkModalState()"
                    :title="'Näytä ' + player.username + ' remmi'"
                  >
                    {{ player.username }}
                  </span>
                  <span v-else class="player-name">{{ player.username }}</span>
                </td>
                <td class="points-column">{{ player.total_points }} p</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Controls -->
        <div class="pagination-container">
          <div class="pagination-info">
            Näytetään {{ leaderboardData.length ? (currentPage - 1) * pageSize + 1 : 0 }} - 
            {{ Math.min(currentPage * pageSize, totalItems) }} / {{ totalItems }} pelaajaa
          </div>
          
          <div class="pagination-controls">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              :class="['pagination-button', {'disabled': currentPage === 1}]"
            >
              Edellinen
            </button>
            
            <button 
              @click="nextPage" 
              :disabled="!hasMorePages"
              :class="['pagination-button', {'disabled': !hasMorePages}]"
            >
              Seuraava
            </button>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- Player Team Modal -->
    <Teleport to="body">
      <PlayerTeamModal
          :show="isPlayerTeamModalVisible"
          :player-id="selectedPlayerId"
          :player-name="selectedPlayerName"
          @close="closePlayerTeamModal"
          @viewHorse="handleViewHorse"
          @viewJockey="handleViewJockey"
          ref="playerTeamModal"
      />
    </Teleport>

    <!-- Horse Performance Modal -->
    <Teleport to="body">
      <HorsePerformanceModal
          :show="showPerformanceModal"
          :horse-id="performanceHorseId"
          :horse-name="performanceHorseName"
          @close="closePerformanceModal"
      />
    </Teleport>

    <!-- Jockey Performance Modal -->
    <Teleport to="body">
      <JockeyPerformanceModal
          :show="showJockeyPerformanceModal"
          :jockey-id="performanceJockeyId"
          :jockey-name="performanceJockeyName"
          @close="closeJockeyPerformanceModal"
      />
    </Teleport>

  </div>
</template>

<script>
import { ref, onMounted, inject, watch } from 'vue';
import axios from '../plugins/axios'; // Use the configured axios instance
import PlayerTeamModal from './PlayerTeamModal.vue'; // Import the player team modal
import HorsePerformanceModal from './HorsePerformanceModal.vue'; // Import the new horse modal
import JockeyPerformanceModal from './JockeyPerformanceModal.vue'; // Import the new jockey modal
import PeriodArchivePage from './PeriodArchivePage.vue';

export default {
  name: 'ResultsPage',
  props: {
    initialView: { type: String, default: 'current' },
    requestedStart: { type: String, default: '' },
  },
  components: {
    PlayerTeamModal,       // Register the player team modal
    HorsePerformanceModal, // Register the horse performance modal
    JockeyPerformanceModal, // Register the jockey performance modal
    PeriodArchivePage,
  },
  setup(props) {
    // --- Leaderboard State ---
    const leaderboardData = ref([]); // Stores { PlayerID, username, total_points }
    const isLoading = ref(false);
    const error = ref(null);
    const isAuthenticated = inject('isAuthenticated'); // Assuming authentication state is provided
    const mainView = ref(props.initialView === 'archive' ? 'archive' : 'current');

    // --- Pagination State ---
    const currentPage = ref(1);
    const pageSize = ref(100); // Number of items per page
    const nextToken = ref(null); // Token for next page
    const hasMorePages = ref(false); // Whether there are more pages
    const totalItems = ref(0); // Estimated total number of items
    const pageTokens = ref({}); // Store tokens for each page for efficient navigation

    // --- Player Team Modal State ---
    const isPlayerTeamModalVisible = ref(false);
    const selectedPlayerId = ref(null);
    const selectedPlayerName = ref('');

    // --- Horse Performance Modal State ---
    const showPerformanceModal = ref(false);
    const performanceHorseId = ref(null);
    const performanceHorseName = ref('');

    // --- Jockey Performance Modal State ---
    const showJockeyPerformanceModal = ref(false);
    const performanceJockeyId = ref(null);
    const performanceJockeyName = ref('');


    // --- Fetch Leaderboard Data Method ---
    const fetchLeaderboard = async (page = 1, token = null) => {
      // Check authentication before fetching
      if (!isAuthenticated.value) {
        console.log("User not authenticated. Cannot fetch leaderboard.");
        leaderboardData.value = [];
        error.value = "Kirjaudu sisään nähdäksesi tulokset.";
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      error.value = null;
      console.log(`Fetching leaderboard data for page ${page}...`);

      try {
        // Build the endpoint URL with pagination parameters
        let endpoint = `${process.env.VUE_APP_API_BASE_URL}/total_points`;
        const params = new URLSearchParams();
        params.append('limit', pageSize.value);
        
        // Use the token if provided, otherwise use the stored token for the requested page
        const pageToken = token || pageTokens.value[page];
        if (pageToken) {
          params.append('next_token', pageToken);
        }
        
        // Append query parameters to the URL
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
        
        console.log(`Calling endpoint: ${endpoint}`);
        const response = await axios.get(endpoint);
        console.log("API Response Received:", response.data);

        // Check if the response has the expected structure
        if (response.data && Array.isArray(response.data.items)) {
          leaderboardData.value = response.data.items;
          
          // Update pagination state
          hasMorePages.value = response.data.has_more || false;
          nextToken.value = response.data.next_token || null;
          
          // Store the token for the next page
          if (nextToken.value) {
            pageTokens.value[page + 1] = nextToken.value;
          }
          
          // Update total items count
          if (response.data.total_count) {
            totalItems.value = response.data.total_count;
          } else {
            // Calculate total based on current page and whether there are more pages
            if (hasMorePages.value) {
              // If there are more pages, we don't know the exact total yet
              // Set a minimum estimate
              totalItems.value = Math.max(totalItems.value, (page - 1) * pageSize.value + leaderboardData.value.length + 1);
            } else {
              // If this is the last page, we can calculate the exact count
              totalItems.value = (page - 1) * pageSize.value + leaderboardData.value.length;
            }
          }
          
          currentPage.value = page;
        } else {
          console.error("API response has unexpected format:", response.data);
          leaderboardData.value = [];
          error.value = "Palvelimelta saatiin odottamaton vastaus.";
        }
      } catch (err) {
        console.error('Error fetching leaderboard:', err.response?.data || err.message);
        error.value = err.response?.data?.error || err.message || 'Tuntematon virhe tapahtui.';
        leaderboardData.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // --- Pagination Methods ---
    const nextPage = () => {
      if (hasMorePages.value) {
        fetchLeaderboard(currentPage.value + 1, nextToken.value);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        fetchLeaderboard(currentPage.value - 1);
      }
    };

    const setMainView = (view) => {
      mainView.value = view;
      if (view === 'current' && leaderboardData.value.length === 0 && !isLoading.value) {
        fetchLeaderboard(1);
      }
    };
    watch(() => props.initialView, view => setMainView(view === 'archive' ? 'archive' : 'current'));

    // --- Player Team Modal Methods ---
    const openPlayerTeamModal = (playerId, playerName) => {
      if (!playerId) {
        console.error("Cannot open team modal without playerId.");
        return;
      }
      
      // First, ensure we're not in the middle of a transition
      if (isPlayerTeamModalVisible.value) {
        console.log("Modal already visible, closing first");
        isPlayerTeamModalVisible.value = false;
        
        // Give it a moment to close before reopening
        setTimeout(() => {
          console.log("Reopening modal for:", playerId, playerName);
          selectedPlayerId.value = playerId;
          selectedPlayerName.value = playerName || 'Selected Player';
          isPlayerTeamModalVisible.value = true;
        }, 100);
      } else {
        console.log("Opening player team modal for:", playerId, playerName);
        selectedPlayerId.value = playerId;
        selectedPlayerName.value = playerName || 'Selected Player';
        isPlayerTeamModalVisible.value = true;
        
        // Force a small delay to ensure Vue has time to update the DOM
        setTimeout(() => {
          if (!isPlayerTeamModalVisible.value) {
            console.log("Modal visibility state was reset - forcing it open");
            isPlayerTeamModalVisible.value = true;
          }
        }, 50);
      }
      
      // Close other modals when opening this one
      closePerformanceModal();
      closeJockeyPerformanceModal();
    };

    const closePlayerTeamModal = () => {
      console.log("Closing player team modal");
      isPlayerTeamModalVisible.value = false;
      // We'll keep the player ID and name in case we need to reopen the modal
      // selectedPlayerId.value = null;
      // selectedPlayerName.value = '';
    };

    // --- Horse Performance Modal Methods ---
    const showHorsePerformance = (horseId, horseName) => {
        if (!horseId) { console.warn("Cannot show performance for horse without ID."); return; }
        performanceHorseId.value = horseId;
        performanceHorseName.value = horseName || 'Selected Horse';
        showPerformanceModal.value = true; // Open the horse modal
        // Close other modals if they might be open
        // isPlayerTeamModalVisible.value = false; // Decide if player modal should close
        closeJockeyPerformanceModal();
    };

    const closePerformanceModal = () => {
        showPerformanceModal.value = false;
        // Clear details when closing
        performanceHorseId.value = null;
        performanceHorseName.value = '';
    };

    // --- Jockey Performance Modal Methods ---
    const showJockeyPerformance = (jockeyId, jockeyName) => {
        if (!jockeyId) { console.warn("Cannot show performance for jockey without ID."); return; }
        performanceJockeyId.value = jockeyId;
        performanceJockeyName.value = jockeyName || 'Selected Jockey';
        showJockeyPerformanceModal.value = true; // Open the jockey modal
        // Close other modals if they might be open
        // isPlayerTeamModalVisible.value = false; // Decide if player modal should close
        closePerformanceModal();
    };

    const closeJockeyPerformanceModal = () => {
        showJockeyPerformanceModal.value = false;
        // Clear details when closing
        performanceJockeyId.value = null;
        performanceJockeyName.value = '';
    };

    // --- Event Handlers from PlayerTeamModal ---
    /**
     * Handles the 'viewHorse' event emitted from PlayerTeamModal.
     * Triggers the Horse Performance modal.
     * @param {object} payload - Object containing { horseId, horseName }.
     */
    const handleViewHorse = (payload) => {
      console.log("Received viewHorse event in ResultsPage:", payload);
      if (payload && payload.horseId) {
        showHorsePerformance(payload.horseId, payload.horseName);
      } else {
        console.warn("Invalid payload received for viewHorse event.");
      }
    };

    /**
     * Handles the 'viewJockey' event emitted from PlayerTeamModal.
     * Triggers the Jockey Performance modal.
     * @param {object} payload - Object containing { jockeyId, jockeyName }.
     */
    const handleViewJockey = (payload) => {
      console.log("Received viewJockey event in ResultsPage:", payload);
      if (payload && payload.jockeyId) {
        showJockeyPerformance(payload.jockeyId, payload.jockeyName);
      } else {
        console.warn("Invalid payload received for viewJockey event.");
      }
    };


    // --- Lifecycle Hook ---
    onMounted(() => {
      if (mainView.value === 'current') fetchLeaderboard(1);
      
      // Add a global event listener to help debug modal issues
      window.addEventListener('keydown', (e) => {
        if (e.key === 'F8') {
          console.log("Debug check - Modal state:", {
            isVisible: isPlayerTeamModalVisible.value,
            playerId: selectedPlayerId.value,
            playerName: selectedPlayerName.value
          });
        }
      });
    });

    // --- Watch for Authentication Changes ---
    watch(isAuthenticated, (newValue, oldValue) => {
      console.log(`Authentication state changed to: ${newValue}`);
      if (newValue !== oldValue) {
        // Reset pagination state
        currentPage.value = 1;
        nextToken.value = null;
        pageTokens.value = {};
        
        fetchLeaderboard(1); // Re-fetch or clear data when auth state changes
        // Also close modals if user logs out
        if (!newValue) {
          closePlayerTeamModal();
          closePerformanceModal();
          closeJockeyPerformanceModal();
        }
      }
    });
    
    // Watch for changes to the modal visibility state to ensure consistency
    watch(isPlayerTeamModalVisible, (newValue) => {
      console.log(`Modal visibility changed to: ${newValue}`, 
        newValue ? `for player: ${selectedPlayerId.value} (${selectedPlayerName.value})` : '');
    });

    // --- Return reactive state and methods ---
    return {
      // Leaderboard
      leaderboardData,
      isLoading,
      error,
      isAuthenticated,
      mainView,
      setMainView,

      // Pagination
      currentPage,
      pageSize,
      hasMorePages,
      totalItems,
      nextPage,
      prevPage,

      // Player Team Modal Control
      isPlayerTeamModalVisible,
      selectedPlayerId,
      selectedPlayerName,
      openPlayerTeamModal,
      closePlayerTeamModal,
      handleViewHorse, // Handler for event from PlayerTeamModal
      handleViewJockey, // Handler for event from PlayerTeamModal
      
      // Debug method to check modal state
      checkModalState: () => {
        console.log("Modal state check:", {
          isVisible: isPlayerTeamModalVisible.value,
          playerId: selectedPlayerId.value,
          playerName: selectedPlayerName.value
        });
      },

      // Horse Performance Modal Control
      showPerformanceModal,
      performanceHorseId,
      performanceHorseName,
      // showHorsePerformance, // Now handled by handleViewHorse
      closePerformanceModal,

      // Jockey Performance Modal Control
      showJockeyPerformanceModal,
      performanceJockeyId,
      performanceJockeyName,
      // showJockeyPerformance, // Now handled by handleViewJockey
      closeJockeyPerformanceModal,
    };
  },
}
</script>

<style scoped>
/* Page Layout */
.page-content {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(17, 34, 64, 0.95) 100%);
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  color: #e6f1ff;
}

.page-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #64ffda;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-description {
  font-size: 1.1rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  color: #a8b2d1;
}

.results-view-toggle {
  display: flex;
  justify-content: center;
  gap: 8px;
  max-width: 460px;
  margin: -18px auto 28px;
  padding: 6px;
  background-color: rgba(10, 25, 47, 0.6);
  border: 1px solid #1e3a5f;
  border-radius: 12px;
}

.results-view-button {
  flex: 1;
  padding: 10px 16px;
  background-color: transparent;
  color: #a8b2d1;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.results-view-button:hover {
  color: #64ffda;
  background-color: rgba(100, 255, 218, 0.08);
}

.results-view-button.active {
  color: #0a192f;
  background-color: #64ffda;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.loading-spinner {
  border: 3px solid rgba(100, 255, 218, 0.3);
  border-radius: 50%;
  border-top: 3px solid #64ffda;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  color: #a8b2d1;
  font-size: 1.1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-message {
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  padding: 20px;
  margin: 30px auto;
  max-width: 800px;
  color: #ff6b6b;
  text-align: center;
}

.error-message strong {
  display: block;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #a8b2d1;
}

.empty-text {
  font-size: 1.2rem;
  margin-bottom: 20px;
}

.auth-prompt {
  font-size: 1rem;
  padding: 15px;
  background-color: rgba(100, 255, 218, 0.1);
  border-radius: 8px;
  display: inline-block;
}

.auth-link {
  color: #64ffda;
  font-weight: 600;
  text-decoration: none;
  padding: 2px 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.auth-link:hover {
  background-color: rgba(100, 255, 218, 0.2);
  text-decoration: underline;
}

/* Leaderboard Container */
.leaderboard-container {
  margin: 20px auto;
}

.leaderboard-card {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  overflow: hidden;
  transition: transform 0.3s ease;
}

.leaderboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 30px -15px rgba(2, 12, 27, 0.8);
}

.leaderboard-header {
  padding: 20px 25px;
  border-bottom: 1px solid #1e3a5f;
  background-color: rgba(30, 58, 95, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.leaderboard-header h2 {
  margin: 0;
  color: #e6f1ff;
  font-size: 1.5rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 10px;
}

.leaderboard-header h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background-color: #64ffda;
}

/* Table Container */
.table-container {
  overflow-x: auto;
  padding: 0;
}

/* Leaderboard Table */
.leaderboard-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.leaderboard-table th {
  padding: 15px 20px;
  background-color: rgba(30, 58, 95, 0.5);
  color: #a8b2d1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
  border-bottom: 2px solid #1e3a5f;
}

.leaderboard-row {
  transition: background-color 0.2s ease;
}

.leaderboard-row:hover {
  background-color: rgba(100, 255, 218, 0.05);
}

.leaderboard-table td {
  padding: 15px 20px;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  color: #e6f1ff;
}

.leaderboard-table tr:last-child td {
  border-bottom: none;
}

/* Column Styling */
.rank-column {
  width: 80px;
  text-align: center;
  font-weight: 600;
  color: #a8b2d1;
}

.player-column {
  width: auto;
}

.points-column {
  width: 120px;
  text-align: right;
  font-weight: 600;
  color: #ffb86c;
}

/* Player Name Styling */
.player-name {
  color: #e6f1ff;
  font-weight: 500;
}

.player-name.clickable {
  color: #64ffda;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 5px 8px;
  border-radius: 4px;
  margin: -5px -8px;
  display: inline-block;
}

.player-name.clickable:hover {
  background-color: rgba(100, 255, 218, 0.1);
  text-decoration: underline;
}

/* Pagination Container */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-top: 1px solid #1e3a5f;
  background-color: rgba(30, 58, 95, 0.3);
  flex-wrap: wrap;
  gap: 15px;
}

.pagination-info {
  color: #a8b2d1;
  font-size: 0.9rem;
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

.pagination-button {
  padding: 8px 16px;
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.pagination-button:hover:not(.disabled) {
  background-color: #52d6c0;
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}

.pagination-button.disabled {
  background-color: rgba(100, 255, 218, 0.3);
  color: rgba(10, 25, 47, 0.7);
  cursor: not-allowed;
  box-shadow: none;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .page-title {
    font-size: 1.8rem;
  }
  
  .page-description {
    font-size: 1rem;
  }

  .results-view-toggle {
    max-width: none;
    margin-top: -16px;
  }
  
  .leaderboard-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-info {
    margin-bottom: 10px;
  }
  
  .leaderboard-table th,
  .leaderboard-table td {
    padding: 12px 15px;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: 20px 15px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .rank-column {
    width: 60px;
  }
  
  .points-column {
    width: 90px;
  }
  
  .leaderboard-table th,
  .leaderboard-table td {
    padding: 10px;
    font-size: 0.9rem;
  }
}
</style>
